import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Modal } from '../components/common/Modal';
import { Button } from '../components/common/Button';
import { IconButton } from '../components/common/IconButton';
import { Icon } from '../components/common/Icon';
import { useMotionConfig } from '../hooks/useMotionConfig';
import { ToastContext, type Toast, type ToastType } from './toast-hooks';
import {
    CONFIRM_EVENT,
    TOAST_EVENT,
    isConfirmEventDetail,
    isToastEventDetail,
} from './toast-events';
export type { Toast, ToastType } from './toast-hooks';

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const [confirmState, setConfirmState] = useState<{
        isOpen: boolean;
        message: string;
        confirmText: string;
        cancelText: string;
        resolve: (val: boolean) => void;
    } | null>(null);

    const showToast = useCallback((message: string, type: ToastType = 'info', options?: { persistent?: boolean; duration?: number; onClick?: () => void }): string => {
        const id = Math.random().toString(36).slice(2, 11);
        const isPersistent = options?.persistent || type === 'progress';
        const duration = options?.duration || 4000;
        
        setToasts(prev => [...prev, { 
            id, 
            message, 
            type,
            persistent: isPersistent,
            duration,
            progress: type === 'progress' ? 0 : undefined,
            onClick: options?.onClick
        }]);

        // Auto remove only non-persistent toasts
        if (!isPersistent) {
            setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== id));
            }, duration);
        }
        
        return id;
    }, []);
    
    const updateToast = useCallback((id: string, updates: Partial<Pick<Toast, 'message' | 'progress' | 'type'>>) => {
        setToasts(prev => prev.map(t => 
            t.id === id ? { ...t, ...updates } : t
        ));
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const confirmDialog = useCallback((message: string, confirmText = 'Confirm', cancelText = 'Cancel') => new Promise<boolean>((resolve) => {
            setConfirmState({
                isOpen: true,
                message,
                confirmText,
                cancelText,
                resolve
            });
        }), []);

    const handleConfirm = useCallback((result: boolean) => {
        if (confirmState) {
            confirmState.resolve(result);
            setConfirmState(null);
        }
    }, [confirmState]);

    // Listen to Bridge Events
    useEffect(() => {
        const handleToastEvent = (event: Event) => {
            if (!(event instanceof CustomEvent) || !isToastEventDetail(event.detail)) return;
            showToast(event.detail.message, event.detail.type, { duration: event.detail.duration });
        };

        const handleConfirmEvent = (event: Event) => {
            if (!(event instanceof CustomEvent) || !isConfirmEventDetail(event.detail)) return;
            const { message, confirmText, cancelText, resolve } = event.detail;
            // We need to hook into the existing promise from the event
            // Using the internal logic
            setConfirmState({
                isOpen: true,
                message,
                confirmText,
                cancelText,
                resolve 
            });
        };

        window.addEventListener(TOAST_EVENT, handleToastEvent, { passive: true });
        window.addEventListener(CONFIRM_EVENT, handleConfirmEvent, { passive: true });

        return () => {
            window.removeEventListener(TOAST_EVENT, handleToastEvent);
            window.removeEventListener(CONFIRM_EVENT, handleConfirmEvent);
        };
    }, [showToast]);

    return (
        <ToastContext.Provider value={{ showToast, updateToast, removeToast, confirmDialog }}>
            {children}
            
            {/* Toast Container */}
            {createPortal(
                <ToastContainer toasts={toasts} removeToast={removeToast} />,
                document.body
            )}

            {/* Confirm Dialog */}
            {confirmState?.isOpen ? <Modal
                    isOpen={true}
                    onClose={() => handleConfirm(false)}
                    size="sm"
                    headless
                >
                    <div className="p-6">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <Icon name="help" className="text-primary" />
                            </div>
                            <p className="text-sm font-medium text-text-light dark:text-text-dark leading-relaxed pt-1">
                                {confirmState.message}
                            </p>
                        </div>
                        
                        <div className="flex gap-3">
                            <Button
                                variant="ghost"
                                onClick={() => handleConfirm(false)}
                                className="flex-1"
                            >
                                {confirmState.cancelText}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => handleConfirm(true)}
                                className="flex-1"
                            >
                                {confirmState.confirmText}
                            </Button>
                        </div>
                    </div>
                </Modal> : null}
        </ToastContext.Provider>
    );
};

/** Animated toast container — wraps each toast in motion.div for enter/exit animations */
const ToastContainer: React.FC<{ toasts: Toast[], removeToast: (id: string) => void }> = ({ toasts, removeToast }) => {
    const motionConfig = useMotionConfig();
    return (
        <div className="fixed bottom-4 right-4 z-9999 flex flex-col gap-2 pointer-events-none">
            <AnimatePresence mode="popLayout">
                {toasts.map(toast => (
                    <motion.div
                        key={toast.id}
                        layout
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 100, scale: 0.95 }}
                        transition={motionConfig.subtle}
                    >
                        <ToastItem toast={toast} onClose={() => removeToast(toast.id)} />
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
};

// Component helper
const ToastItem: React.FC<{ toast: Toast, onClose: () => void }> = ({ toast, onClose }) => {
    const { message, type, progress, onClick } = toast;
    
    let bgClass = 'bg-surface-light dark:bg-surface-dark border-gray-200 dark:border-border-dark';
    let icon = 'info';
    let colorClass = 'text-primary';

    switch (type) {
        case 'success':
            bgClass = 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/30';
            icon = 'check_circle';
            colorClass = 'text-green-500';
            break;
        case 'error':
            bgClass = 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30';
            icon = 'error';
            colorClass = 'text-red-500';
            break;
        case 'warning':
            bgClass = 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/30';
            icon = 'warning';
            colorClass = 'text-orange-500';
            break;
        case 'progress':
            bgClass = 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/30';
            icon = 'sync';
            colorClass = 'text-blue-500 animate-spin';
            break;
    }

    const handleClick = () => {
        if (onClick) {
            onClick();
        }
    };

    return (
        <>
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: Toast cards may contain a nested close button; keyboard-safe toast activation needs a separate notification action model. */}
            <div
                className={`px-4 py-3 rounded-xl shadow-2xl border flex flex-col gap-2 pointer-events-auto transition-all duration-300 min-w-72 max-w-sm ${bgClass} ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
                onClick={handleClick}
            >
                <div className="flex items-center gap-3">
                    <Icon name={icon} className={colorClass} />
                    <div className="flex-1 text-sm font-medium text-text-light dark:text-text-dark">{message}</div>
                    <IconButton
                        icon="close"
                        onClick={(e) => { e.stopPropagation(); onClose(); }}
                        variant="ghost"
                        size="sm"
                        label="Close notification"
                    />
                </div>
                {/* Progress bar for progress toasts */}
                {type === 'progress' && progress !== undefined && (
                    <div className="w-full h-1.5 bg-blue-100 dark:bg-blue-800/30 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-blue-500 transition-all duration-300 ease-out rounded-full"
                            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                        />
                    </div>
                )}
            </div>
        </>
    );
};





